import { useEffect, useState } from 'react'
// material
import {
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Divider,
  ListItemButton,
  Skeleton,
} from '@mui/material'
import { styled } from '@mui/material/styles'
// logic
import {
  PolicyType,
  PrivacyType,
  UserPolicy,
} from '@weblite/ts-action/user-policy'
import { useAsyncFunction } from '$func/async-function.react-hook'
import { nothing } from '$func/utils'
import { getUserPolicy, setPrivacyPolicy } from '$logic/user-policy'
// common
import { PageWithAppBar } from '$common/page-with-app-bar'
import { AppBarBackTitleConfirm } from '$common/app-bar/app-bar-back-title-confirm'
import { SettingsSection } from '$common/settings-section'
// localization
import { t } from '$i18n'
import { SettingsItemWithText } from '$common/settings-item'
import {
  closePrivacyTypeSettings,
  openAddPrivacyExceptionUsers,
  openPrivacyExceptionUsers,
} from '$logic/router'

const StyledFormControlLabel = styled(FormControlLabel)`
  width: 100%;
  height: 100%;

  margin: 0;
  justify-content: space-between;
`

const StyledListItemButton = styled(ListItemButton)`
  height: 50px;
  padding: 0 20px;
`

const IntendedDivider = styled(Divider)`
  margin-right: 20px;
`

const AddExceptionsSkeletonContainer = styled('div')`
  width: 100%;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`
const PRIVACY_TYPE_EVENT: Record<PrivacyType, string> = {
  ADD_HALL: 'PROFILE_SETTING_JOIN_CHATS',
  IMAGE: 'PROFILE_SETTING_PROFILE_PIC',
  STATUS: 'PROFILE_SETTING_LAST_SEEN',
  PHONE: 'PROFILE_SETTING_PHONE_NUMBER',
  LINK_VIA_FORWARD: 'PROFILE_SETTING_FORWARD_MESSAGE',
}
interface Props {
  privacyType: PrivacyType
}

// TODO: Use ListItemButton for RadioButtons and exception items
export function PrivacyTypeSettings({ privacyType }: Props) {
  const { value: userPolicy } = useAsyncFunction<UserPolicy>(
    () => getUserPolicy(privacyType),
    [],
  )

  useEffect(() => {
    if (userPolicy) setPolicy(userPolicy.policy)
  }, [userPolicy])

  const [policy, setPolicy] = useState<PolicyType>()
  // TODO skeleton not needed if cache is enabled b/c it should be cached in the prev. page

  const onConfirm = async () => {
    if (policy && userPolicy?.policy !== policy) {
      const succeed = await setPrivacyPolicy(privacyType, policy)
      if (succeed) {
        analite(PRIVACY_TYPE_EVENT[privacyType], { mode: policy })
        await closePrivacyTypeSettings()
      }
    }
  }

  const excludedCount = userPolicy?.exclude.length
  const includedCount = userPolicy?.include.length

  return (
    <PageWithAppBar
      appBar={
        <AppBarBackTitleConfirm
          title={t(`settings.privacy.type.${privacyType.toLowerCase()}`)}
          onBack={closePrivacyTypeSettings}
          onConfirm={onConfirm}
          confirmDisabled={!userPolicy || userPolicy.policy === policy}
        />
      }
    >
      <SettingsSection
        headerText={t(
          `settings.privacy.questions.${privacyType.toLowerCase()}`,
        )}
      >
        <FormControl component="fieldset" fullWidth onChange={nothing}>
          <RadioGroup
            name="controlled-radio-buttons-group"
            value={policy}
            onChange={event => setPolicy(event.target.value as PolicyType)}
          >
            <StyledListItemButton>
              <StyledFormControlLabel
                value="EVERY_BODY"
                disableTypography
                control={<Radio />}
                disabled={!userPolicy}
                label={
                  <Typography variant="body2">
                    {t('settings.privacy.policy.every_body')}
                  </Typography>
                }
                labelPlacement="start"
              />
            </StyledListItemButton>

            <IntendedDivider />

            <StyledListItemButton>
              <StyledFormControlLabel
                value="CONTACTS"
                control={<Radio />}
                disabled={!userPolicy}
                disableTypography
                label={
                  <Typography variant="body2">
                    {t('settings.privacy.policy.contacts')}
                  </Typography>
                }
                labelPlacement="start"
              />
            </StyledListItemButton>

            <IntendedDivider />

            <StyledListItemButton>
              <StyledFormControlLabel
                value="NO_BODY"
                control={<Radio />}
                disabled={!userPolicy}
                disableTypography
                label={
                  <Typography variant="body2">
                    {t('settings.privacy.policy.no_body')}
                  </Typography>
                }
                labelPlacement="start"
              />
            </StyledListItemButton>
          </RadioGroup>
        </FormControl>
      </SettingsSection>

      <SettingsSection headerText={t('settings.privacy.exception.add')}>
        {policy ? (
          <>
            {policy !== 'EVERY_BODY' && (
              <SettingsItemWithText
                text={t('settings.privacy.exception.always')}
                secondaryText={
                  includedCount
                    ? t('settings.privacy.exception.exceptionCount')(
                        includedCount,
                      )
                    : t('settings.privacy.exception.addUsers')
                }
                onClick={() => {
                  if (includedCount)
                    openPrivacyExceptionUsers(privacyType, 'always')
                  else openAddPrivacyExceptionUsers(privacyType, 'always')
                }}
              />
            )}
            {policy !== 'NO_BODY' && (
              <SettingsItemWithText
                text={t('settings.privacy.exception.never')}
                secondaryText={
                  excludedCount
                    ? t('settings.privacy.exception.exceptionCount')(
                        excludedCount,
                      )
                    : t('settings.privacy.exception.addUsers')
                }
                onClick={() => {
                  if (excludedCount)
                    openPrivacyExceptionUsers(privacyType, 'never')
                  else openAddPrivacyExceptionUsers(privacyType, 'never')
                }}
              />
            )}
          </>
        ) : (
          <AddExceptionsSkeletonContainer>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" width="20%" />
          </AddExceptionsSkeletonContainer>
        )}
      </SettingsSection>
    </PageWithAppBar>
  )
}
